import { AspenCandidateAppointmentsService } from '@om1/aspen-api/codegen/client/services/AspenCandidateAppointmentsService'
import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'

/**
 * Coordinator appointment actions (APP-1221, D9 staff side).
 *
 * ## Errors are surfaced, never swallowed
 *
 * Each action maps the backend's status codes onto a message that tells the
 * coordinator what actually happened and whether to retry. That matters more
 * here than in most places: a cancel that silently failed leaves the
 * coordinator believing a slot was freed when the patient is still booked, and
 * they find out when nobody joins the call.
 *
 * ## The caller must refetch
 *
 * These deliberately do not mutate any local row. Status changes arrive via
 * Cal.com webhooks (BOOKING_CANCELLED / BOOKING_NO_SHOW_UPDATED), so the
 * truthful thing to render is what the API says on the next read, not an
 * optimistic guess. Reschedule is the exception — the backend writes the new
 * time itself, because Cal.com does not fire BOOKING_RESCHEDULED for
 * API-initiated reschedules — but a refetch is still how the UI learns it.
 */

type ApiError = { status?: number; body?: { detail?: unknown } }

const detailOf = (error: unknown): string | null => {
    const body = (error as ApiError)?.body?.detail
    if (typeof body === 'string') return body
    if (Array.isArray(body)) {
        const first = body[0] as { msg?: unknown } | undefined
        if (first && typeof first.msg === 'string') return first.msg
    }
    return null
}

/** Backend detail codes → coordinator-facing wording. */
const MESSAGES: Record<string, string> = {
    appointment_not_found: 'This candidate has no appointment to act on.',
    appointment_has_no_calcom_booking: 'This is a legacy preferred-contact time, not a booked appointment, so it cannot be changed here.',
    slot_after_cutoff: 'That time is past the 4pm ET cutoff for the next day. Please choose a later slot.',
    slot_unavailable: 'That time is no longer available. Please choose another.',
    cancel_rejected: 'Cal.com would not cancel this booking — it may already be cancelled or in the past.',
    no_show_rejected: 'Cal.com would not mark a no-show — the appointment may not have happened yet.',
    scheduler_unavailable: 'The scheduler is not configured for this environment.'
}

const messageFor = (error: unknown, fallback: string): string => {
    const detail = detailOf(error)
    if (detail && MESSAGES[detail]) return MESSAGES[detail]
    const status = (error as ApiError)?.status
    if (status === 502 || status === 503) {
        // Distinguished from a rejection: the coordinator's next move is to try
        // again, not to pick different inputs.
        return 'Cal.com could not be reached. Nothing was changed — please try again in a moment.'
    }
    return fallback
}

export const useAppointmentActions = () => {
    const { enqueueSnackbar } = useSnackbar()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const run = useCallback(
        async (action: () => Promise<unknown>, success: string, fallback: string): Promise<boolean> => {
            setIsSubmitting(true)
            try {
                await action()
                enqueueSnackbar(success, { variant: 'success' })
                return true
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Appointment action failed:', error)
                enqueueSnackbar(messageFor(error, fallback), { variant: 'error' })
                return false
            } finally {
                setIsSubmitting(false)
            }
        },
        [enqueueSnackbar]
    )

    const cancelAppointment = useCallback(
        (candidateId: string, reason?: string) =>
            run(
                () =>
                    AspenCandidateAppointmentsService.cancelAppointmentCandidatesCandidateIdAppointmentCancelPost({
                        candidateId,
                        requestBody: { reason: reason || null }
                    }),
                'Appointment cancelled. The slot is now free for other patients.',
                'Could not cancel the appointment.'
            ),
        [run]
    )

    const rescheduleAppointment = useCallback(
        (candidateId: string, slotStart: string, reason?: string) =>
            run(
                () =>
                    AspenCandidateAppointmentsService.rescheduleAppointmentCandidatesCandidateIdAppointmentReschedulePost({
                        candidateId,
                        requestBody: { slotStart, reason: reason || null }
                    }),
                'Appointment rescheduled.',
                'Could not reschedule the appointment.'
            ),
        [run]
    )

    const markNoShow = useCallback(
        (candidateId: string, hostAbsent = false) =>
            run(
                () =>
                    AspenCandidateAppointmentsService.markAppointmentNoShowCandidatesCandidateIdAppointmentNoShowPost({
                        candidateId,
                        requestBody: { hostAbsent }
                    }),
                // Worded to match reality: Cal.com's webhook is what moves our
                // row, so the list may take a moment to catch up.
                'No-show recorded. The status will update once Cal.com confirms it.',
                'Could not mark a no-show.'
            ),
        [run]
    )

    return { cancelAppointment, rescheduleAppointment, markNoShow, isSubmitting }
}
